import { z } from "zod";
export const healthIndicatorSchema = z.object({
    metric: z.string(),
    status: z.enum(["green", "yellow", "red"]),
    value: z.string(),
    detail: z.string().optional(),
});
/**
 * One recorded rollup prune, as the UI needs to draw it.
 *
 * `orphanBlocks` / `orphanTxs` are nullable because the five events reconstructed by migration
 * 0042 predate the recording and those figures are unrecoverable — `reconstructed` says which
 * kind of row you are looking at rather than leaving the nulls looking like a defect.
 */
export const chainReorgSchema = z.object({
    detectedAt: z.string(),
    anchorBlock: z.number(),
    anchorSlot: z.number().nullable(),
    anchorCheckpoint: z.number().nullable(),
    /** Slot span of the blocks actually discarded. Narrower than the gap in the chain. */
    orphanFirstSlot: z.number().nullable(),
    orphanLastSlot: z.number().nullable(),
    orphanBlocks: z.number().nullable(),
    orphanCheckpoints: z.number().nullable(),
    orphanTxs: z.number().nullable(),
    provenEpochAtPrune: z.number().nullable(),
    deadlineEpoch: z.number().nullable(),
    reconstructed: z.boolean(),
});
export const chainHealthResponseSchema = z.object({
    // Timing
    latestBlock: z.number(),
    latestBlockTimestamp: z.number().nullable(),
    latestSlot: z.number().nullable(),
    slotDuration: z.number(),
    genesisTime: z.number().nullable(),
    avgBlockTime: z.number(),
    blockTimeStdDev: z.number(),
    // Utilization
    avgManaUtilizationPct: z.number().nullable(),
    avgTxsPerBlock: z.number(),
    emptyBlockPct: z.number(),
    missedSlotPct: z.number(),
    // Finality pipeline. For each stage we surface both the block-level cursor
    // and the checkpoint / epoch that block belongs to, so the UI can clarify
    // which unit each stage operates on:
    //   - proposed:     the head block (always block-level; checkpoint may not
    //                   exist yet for blocks not yet sealed into one).
    //   - checkpointed: highest block sealed into a checkpoint that landed on L1.
    //   - proven:       highest block in a checkpoint whose proof is on L1.
    //   - finalized:    highest block in a checkpoint past the L1 finality window.
    // checkpointNumber is null for "proposed" when the head block has no
    // assigned checkpoint yet, and for any stage on a brand-new chain at 0.
    // epochNumber is null only when the underlying block has no slot number
    // recorded (early-version backfill rows).
    proposedBlock: z.number(),
    proposedCheckpoint: z.number().nullable(),
    proposedEpoch: z.number().nullable(),
    checkpointedBlock: z.number(),
    checkpointedCheckpoint: z.number().nullable(),
    checkpointedEpoch: z.number().nullable(),
    provenBlock: z.number(),
    provenCheckpoint: z.number().nullable(),
    provenEpoch: z.number().nullable(),
    finalizedBlock: z.number(),
    finalizedCheckpoint: z.number().nullable(),
    finalizedEpoch: z.number().nullable(),
    checkpointLag: z.number(),
    proofLag: z.number(),
    // The rollup's proof deadline for the pending chain, which is what turns `proofLag` from a
    // lag number into a countdown. The rollup accepts an epoch's proof while the current epoch is
    // below `epoch + proofSubmissionEpochs + 1` (TimeLib.toDeadlineEpoch); once that passes with
    // no proof it winds the pending tip back to the proven tip and every checkpoint after it is
    // discarded, L1-published or not.
    //
    // Only the stable facts are sent. Remaining headroom is `deadlineSlot - currentSlot`, derived
    // on the client against the wall clock so it counts down between fetches instead of stepping
    // whenever a poll happens to land.
    //
    // Null when the network's `aztec_proof_submission_epochs` hasn't been read from L1 yet — the
    // window is genuinely unknown then, and the UI renders nothing rather than assuming a value.
    proofWindow: z
        .object({
        proofSubmissionEpochs: z.number(),
        /** Max possible headroom, `(proofSubmissionEpochs + 1) × slotsPerEpoch`. The denominator. */
        windowSlots: z.number(),
        /**
         * Epoch of the oldest checkpoint on L1 that is not yet proven — the one whose deadline
         * governs. Null when the pending chain is fully proven, i.e. nothing is at risk.
         */
        oldestUnprovenEpoch: z.number().nullable(),
        deadlineEpoch: z.number().nullable(),
        /** First slot at which the rollup may prune. Null whenever `oldestUnprovenEpoch` is. */
        deadlineSlot: z.number().nullable(),
    })
        .nullable(),
    // Prunes that overlap the epoch being viewed, newest first. Empty on the overwhelming majority
    // of epochs; non-empty is the signal that this epoch's numbers describe work the chain later
    // threw away. Carried on the health payload rather than fetched separately because the epoch
    // timeline cannot be drawn honestly without it.
    reorgs: z.array(chainReorgSchema),
    // Throughput
    tps: z.number(),
    // Health scores
    overallHealth: z.enum(["healthy", "degraded", "unhealthy"]),
    indicators: z.array(healthIndicatorSchema),
    // Sparklines over a rolling window of slots ending at the requested epoch — the newest
    // block when that epoch is the live one — with one entry per slot. Rolling rather than
    // epoch-scoped on purpose: an epoch-scoped series starts each epoch with one or two points
    // and reads as broken for the first minutes of every epoch. Anchored to the selection all
    // the same, so browsing back an epoch doesn't leave live slots on screen.
    //
    // `fromSlot`/`toSlot` are reported so the caption states the real window instead of the
    // UI hardcoding one — the previous caption said "last 64 blocks" while the query meant
    // something else again. Length is always toSlot - fromSlot + 1.
    //
    // null means no block at that slot, which is deliberately distinct from zero: a missed
    // slot should read as a gap in the line, not as a dip to the floor.
    //
    // Everything here is per SLOT, and a slot carries a whole checkpoint — one or more L2 blocks,
    // all sharing the slot's timestamp. So `blockTimes` is the interval between slots (recorded
    // for a slot's first block; a later one would report a 0s gap), and `txCounts`/`manaUsed` are
    // sums across the slot's blocks. None of them is a per-block figure on its own: mainnet runs
    // ~3.3 blocks per checkpoint, so a consumer that reads `blockTimes` as block time reports the
    // checkpoint cadence — a number pinned to the slot duration — and one that reads `manaUsed` as
    // mana per block overstates it threefold. `blockCounts` is the divisor that converts them, and
    // the reason it is in the payload at all.
    sparklines: z.object({
        fromSlot: z.number(),
        toSlot: z.number(),
        blockTimes: z.array(z.number().nullable()),
        blockCounts: z.array(z.number().nullable()),
        txCounts: z.array(z.number().nullable()),
        manaUsed: z.array(z.number().nullable()),
    }),
    // The actual current epoch (by wall clock / genesis time), used for pagination.
    // May differ from epochTimeline.epochNumber when the network is stalled.
    currentEpochNumber: z.number().nullable(),
    // Epoch timeline with per-slot status from the sentinel taxonomy.
    // `blocksPerSlot` is how many L2 blocks landed at that slot (almost always 0 or 1).
    // `slotStatuses` maps slot number (as string) to sentinel verdict; slots missing
    // from the map are unknown (pre-indexing or sentinel data expired).
    // `slotDetails` augments slots that have any block(s) with the contained
    // block range and the checkpoint that block belongs to, for hover details
    // in the UI. Slots with zero blocks have no entry.
    epochTimeline: z.object({
        epochNumber: z.number(),
        epochStartSlot: z.number(),
        slotsPerEpoch: z.number(),
        blocksPerSlot: z.record(z.string(), z.number()),
        slotStatuses: z.record(z.string(), z.enum([
            // v5 six-case proposer taxonomy (see schema.ts slotStatusEnum).
            "checkpoint-mined",
            "checkpoint-valid",
            "checkpoint-invalid",
            "checkpoint-unvalidated",
            "checkpoint-missed",
            "blocks-missed",
            "unknown",
        ])),
        // Slots in this epoch whose work a recorded prune discarded. Sent as a list rather than folded
        // into `slotStatuses`, because the status is still honestly `checkpoint-mined` — the proposer
        // really did mine a checkpoint — and what changed is what the chain later did with it. Keeping
        // the two apart means the timeline can say "mined, then discarded" instead of having to choose.
        orphanedSlots: z.array(z.number()),
        slotDetails: z.record(z.string(), z.object({
            firstBlock: z.number(),
            lastBlock: z.number(),
            checkpointNumber: z.number().nullable(),
        })),
    }).nullable(),
});
//# sourceMappingURL=health.js.map